import { useCallback, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { EXPERIENCE_TYPEAHEAD_SEARCH_TYPES, TRANSFER_SEARCH_VERTICAL } from 'constants/experience'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useExperiment from 'hooks/Experiments/useExperiment'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { useTypeaheadSubmit } from 'hooks/Search/useTypeaheadSubmit'
import useQueryParams from 'hooks/useQueryParams'
import {
  createExperienceNaturalLanguageItem,
  isExperienceNaturalLanguageItem,
} from 'lib/search/experienceNaturalLanguageSearch'

export default function useExperienceNaturalLanguageSearch(isTransferSearch = false) {
  const enabled = useExperienceNaturalLanguageSearchEnabled({ isTransferSearch })
  const submitRef = useRef<App.TypeaheadSubmitRefData | null>(null)
  const { dispatchTypeaheadExtraction } = useTypeaheadSubmit({ enabled, typeaheadTypes: EXPERIENCE_TYPEAHEAD_SEARCH_TYPES })

  const getSearchItem = useCallback((selectedItem?: App.SearchItem, fallbackItem?: App.SearchItem): App.SearchItem => {
    if (!enabled) return selectedItem ?? fallbackItem ?? ANYWHERE_SEARCH_ITEM
    if (selectedItem && !isExperienceNaturalLanguageItem(selectedItem)) return selectedItem

    const refData = submitRef.current
    const query = (selectedItem?.value ?? refData?.searchPhrase)?.trim()
    if (!query || !refData) return selectedItem ?? fallbackItem ?? ANYWHERE_SEARCH_ITEM

    const item = createExperienceNaturalLanguageItem(query, refData.typeaheadId)
    dispatchTypeaheadExtraction({ ...refData, searchPhrase: query }, item)
    return item
  }, [enabled, dispatchTypeaheadExtraction])

  return { getSearchItem, temporarySearchSubmitRef: enabled ? submitRef : undefined }
}

interface ExperienceNaturalLanguageSearchOptions {
  canActivate?: boolean
  isTransferSearch?: boolean
  queryParams?: URLSearchParams
}

export function useExperienceNaturalLanguageSearchEnabled({
  canActivate = false,
  isTransferSearch = false,
  queryParams,
}: ExperienceNaturalLanguageSearchOptions = {}) {
  const currentQueryParams = useQueryParams()
  const isTransfer = isTransferSearch || (queryParams ?? currentQueryParams).get('vertical') === TRANSFER_SEARCH_VERTICAL
  const streamingEnabled = useFeatureFlag(OptimizelyFeatureFlags.experiencesSearchStreaming)
  const experimentEligible = streamingEnabled && !isTransfer
  const { isAnyVariant } = useExperiment(Experiments.searchExperiencesNls, {
    canActivate: canActivate && experimentEligible,
  })

  return experimentEligible && isAnyVariant
}

/** Activate both cohorts only when the controls are visible, not while hidden by CSS. */
export function useExperienceNaturalLanguageSearchActivation(isTransferSearch = false) {
  const [ref, inView] = useInView()
  useExperienceNaturalLanguageSearchEnabled({
    canActivate: inView,
    isTransferSearch,
  })
  return ref
}
